import { useEffect, useRef } from 'react';
import { isValidShippingDetails } from '../helper';
import { apiBaseUrl } from '../../../util/api';
import axios from 'axios';
import { getOrderParams } from '../CheckoutPageWithPayment';
import { getProcess } from '../../../transactions/transaction';
import { hasTransactionPassedPendingPayment } from '../CheckoutPageTransactionHelpers';

export const getUserShippingDetails = values => {
  const {
    shippingType,
    recipientName,
    recipientPhoneNumber,
    recipientEmail,
    selectedLocker,
    addressType,
    location,
  } = values;
  const extraDataMaybe = {
    name: recipientName,
    ...(recipientPhoneNumber ? { phone: recipientPhoneNumber } : {}),
  };
  let destinationDetails = {};
  const { address, origin, bounds, ...rest } = location?.selectedPlace || {};
  if (shippingType === 'door') {
    destinationDetails = {
      ...extraDataMaybe,
      addressType,
      address: location.selectedPlace.address,
      origin: { lat: location.selectedPlace.origin.lat, lng: location.selectedPlace.origin.lng },
      selectedPlace: { ...rest },
    };
  } else if (shippingType === 'locker') {
    destinationDetails = { ...extraDataMaybe, lockerCode: selectedLocker.value };
  }
  const customerDetails = {
    ...extraDataMaybe,
    email: recipientEmail,
  };
  return { customerDetails, customerShippingType: shippingType, destinationDetails };
};

export function useShippingRateCalculate({
  values,
  fetchSpeculatedTransaction,
  setShippingRateFetchStatus,
  config,
  pageData,
  setPageData,
  dataLoadInProgress,
}) {
  const formValuesRef = useRef({ msg: '', formValues: {}, loading: false });
  const fetchLineItem = pageData => {
    const orderParams = getOrderParams(pageData, {}, {}, config);

    const tx = pageData ? pageData.transaction : null;
    const pageDataListing = pageData.listing;
    const processName =
      tx?.attributes?.processName ||
      pageDataListing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
    const process = processName ? getProcess(processName) : null;

    // If transaction has passed payment-pending state, speculated tx is not needed.
    const shouldFetchSpeculatedTransaction =
      !!pageData?.listing?.id &&
      !!pageData.orderData &&
      !!process &&
      !hasTransactionPassedPendingPayment(tx, process);

    if (shouldFetchSpeculatedTransaction) {
      const processAlias = pageData.listing.attributes.publicData?.transactionProcessAlias;
      const transactionId = tx ? tx.id : null;
      const isInquiryInPaymentProcess =
        tx?.attributes?.lastTransition === process.transitions.INQUIRE;

      const requestTransition = isInquiryInPaymentProcess
        ? process.transitions.REQUEST_PAYMENT_AFTER_INQUIRY
        : process.transitions.REQUEST_PAYMENT;
      const isPrivileged = process.isPrivileged(requestTransition);

      return fetchSpeculatedTransaction(
        orderParams,
        processAlias,
        transactionId,
        requestTransition,
        isPrivileged
      );
    }
  };
  const isEqual = (value, refValue) => {
    return value === refValue;
  };
  const fetchShippingRates = async values => {
    try {
      if (formValuesRef.current.loading) {
        return;
      }
      formValuesRef.current.loading = true;
      let updatedPageData = {
        ...pageData,
        formValues: values,
      };
      setPageData(updatedPageData);
      setShippingRateFetchStatus(true);
      const details = getUserShippingDetails(values);
      const url = `${apiBaseUrl()}/api/shipping/multi-item/shipping-rates`;
      const resp = await axios.post(url, details, { withCredentials: true });
      const { shippingCharge } = resp.data;
      updatedPageData = {
        ...pageData,
        orderData: { ...pageData.orderData, shippingCharge },
      };
      await fetchLineItem(updatedPageData);
      formValuesRef.current.loading = false;
      setShippingRateFetchStatus(false);
    } catch (err) {
      setShippingRateFetchStatus(false, err);
    }
  };

  const shouldPreventRateReloading = values => {
    const isMessageChanged = !isEqual(values.initialMessage, formValuesRef.current.msg);
    const isNameChanged =
      !isEqual(values.name, formValuesRef.current.formValues.name) ||
      !isEqual(values.recipientName, formValuesRef.current.formValues.recipientName);
    const isEmailChanged =
      !isEqual(values.recipientEmail, formValuesRef.current.formValues.recipientEmail) ||
      !isEqual(values.tempEmail, formValuesRef.current.formValues.tempEmail);
    const isPhoneNumberChanged = !isEqual(
      values.recipientPhoneNumber,
      formValuesRef.current.formValues.recipientPhoneNumber
    );
    if (isMessageChanged || isNameChanged || isEmailChanged || isPhoneNumberChanged) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const shouldStopReloading = shouldPreventRateReloading(values);
    formValuesRef.current.msg = values.initialMessage;
    formValuesRef.current.formValues = values;
    if (!isValidShippingDetails(values) || dataLoadInProgress || shouldStopReloading) {
      return;
    }

    fetchShippingRates(values);
  }, [values]);
}
